import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Calendar,
  Car,
  Clock,
  Phone,
  Search,
  Truck,
  User,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  MapPin,
} from "lucide-react";

const API_URL = "https://rocaresteticaautomotiva.com.br/api";

// ─── shared dark-theme tokens ───────────────────────────────────────────────
const T = {
  bg: "#0a0a0f",
  surface: "#0d0d1a",
  card: (grad, border) => ({ background: grad, border: `1px solid ${border}`, borderRadius: 16, padding: 20 }),
  gold: "#e8c84a",
  blue: "#74c0fc",
  purple: "#c77dff",
  green: "#69db7c",
  orange: "#ff9f43",
  red: "#ff6b6b",
  yellow: "#ffd43b",
  text: "#e0e0e0",
  muted: "#888",
  border: "#222",
};

function pill(color, bg) {
  return {
    display: "inline-block", padding: "3px 12px", borderRadius: 50,
    fontSize: 12, fontWeight: 600, background: bg, color,
  };
}

function StatusBadge({ status }) {
  const map = {
    "Finalizado":   [T.green,  "rgba(105,219,124,0.15)"],
    "Em andamento": [T.yellow, "rgba(255,212,59,0.15)"],
    "Cancelado":    [T.red,    "rgba(255,107,107,0.15)"],
    "Agendado":     [T.blue,   "rgba(116,192,252,0.15)"],
  };
  const [color, bg] = map[status] || [T.blue, "rgba(116,192,252,0.15)"];
  return <span style={pill(color, bg)}>{status || "Agendado"}</span>;
}

// ─── canvas background (same as other pages) ────────────────────────────────
function useAnimatedCanvas(ref) {
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: 40 }, () => ({
      x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight,
      r: Math.random() * 1.5 + 0.3, vx: (Math.random() - 0.5) * 0.3,
      vy: -(Math.random() * 0.4 + 0.1), life: Math.random(),
      maxLife: Math.random() * 0.5 + 0.3,
      color: Math.random() > 0.5 ? "#e8c84a" : "#ff6b35",
    }));
    const smokes = [];
    const smokeId = setInterval(() => {
      smokes.push({ x: Math.random() * canvas.width * 0.6 + canvas.width * 0.2, y: canvas.height,
        r: 8 + Math.random() * 10, vx: (Math.random() - 0.5) * 0.6,
        vy: -(Math.random() * 0.8 + 0.4), alpha: 0.12 + Math.random() * 0.08,
        grow: 0.8 + Math.random() * 0.6 });
    }, 280);
    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const g = ctx.createLinearGradient(0, 0, 0, canvas.height);
      g.addColorStop(0, "#0a0a0f"); g.addColorStop(1, "#0d0d1a");
      ctx.fillStyle = g; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "rgba(232,200,74,0.04)"; ctx.lineWidth = 1;
      for (let x = 0; x < canvas.width; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke(); }
      for (let y = 0; y < canvas.height; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke(); }
      for (let i = smokes.length - 1; i >= 0; i--) {
        const s = smokes[i]; s.x += s.vx; s.y += s.vy; s.r += s.grow * 0.15; s.alpha -= 0.0015;
        if (s.alpha <= 0) { smokes.splice(i, 1); continue; }
        const sg = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r);
        sg.addColorStop(0, `rgba(180,180,200,${s.alpha})`); sg.addColorStop(1, "rgba(100,100,120,0)");
        ctx.fillStyle = sg; ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill();
      }
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.life -= 0.003;
        if (p.life <= 0 || p.y < -10) { p.x = Math.random() * canvas.width; p.y = canvas.height + 10; p.life = p.maxLife; }
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fillStyle = p.color; ctx.fill();
      });
      animId = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(animId); clearInterval(smokeId); window.removeEventListener("resize", resize); };
  }, []);
}

// ─── main component ──────────────────────────────────────────────────────────
export default function DashboardArea51() {
  const canvasRef = useRef(null);
  useAnimatedCanvas(canvasRef);

  const [agendamentos, setAgendamentos] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [dashboard, setDashboard] = useState({ totalAgendamentos: 0, totalPickup: 0, totalFinalizados: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString("sv-SE"));

  async function fetchAgendamentos() {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/agendamentos?date=${selectedDate}`);
      setAgendamentos(await res.json());
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }

  async function fetchDashboard() {
    try {
      const res = await fetch(`${API_URL}/dashboard?date=${selectedDate}`);
      setDashboard(await res.json());
    } catch (e) { console.error(e); }
  }

  async function fetchHistorico() {
    try {
      const res = await fetch(`${API_URL}/agendamentos?status=Finalizado`);
      setHistorico(await res.json());
    } catch (e) { console.error(e); }
  }

  async function updateStatus(id, status) {
    try {
      await fetch(`${API_URL}/agendamentos/${id}/status`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      fetchAgendamentos(); fetchDashboard(); fetchHistorico();
    } catch (e) { console.error(e); }
  }

  useEffect(() => { fetchAgendamentos(); fetchDashboard(); fetchHistorico(); }, [selectedDate]);
  useEffect(() => {
    const iv = setInterval(() => { fetchAgendamentos(); fetchDashboard(); fetchHistorico(); }, 30000);
    return () => clearInterval(iv);
  }, [selectedDate]);

  const filtered = useMemo(() =>
    agendamentos.filter(i => i.name?.toLowerCase().includes(search.toLowerCase())),
    [agendamentos, search]
  );

  const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("pt-BR");

  // ─── styles ───
  const inp = {
    background: "#0a0a14", border: "1px solid #333", borderRadius: 10,
    color: T.text, fontSize: 13, padding: "9px 12px", outline: "none",
  };

  const actionBtn = (bg, color = "#0a0a0f") => ({
    padding: "7px 14px", borderRadius: 10, border: "none",
    background: bg, color, fontSize: 12, fontWeight: 700, cursor: "pointer",
  });

  return (
    <div style={{ minHeight: "100vh", background: T.bg, position: "relative", overflow: "hidden", fontFamily: "sans-serif", color: T.text }}>
      <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none" }} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1100, margin: "0 auto", padding: "28px 16px 48px" }}>

        {/* ── HEADER ── */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 64, height: 64, margin: "0 auto 12px", borderRadius: "50%", background: "rgba(232,200,74,0.15)", border: "2px solid rgba(232,200,74,0.35)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>
            🚗
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: 2, color: T.gold }}>ÁREA 51</h1>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: T.muted, letterSpacing: 1 }}>Dashboard · Controle Operacional</p>
        </div>

        {/* ── DATE BAR ── */}
        <div style={{ ...T.card("linear-gradient(135deg,#0d1b2a,#1a3a5c)", "#1e4a78"), display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <Calendar size={18} color={T.blue} />
          <input
            type="date" value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            style={{ ...inp, border: "1px solid #1e4a78", color: T.gold }}
          />
          <button onClick={() => setSelectedDate(new Date().toLocaleDateString("sv-SE"))}
            style={actionBtn(T.blue)}>Hoje</button>
          <button onClick={() => { const t = new Date(); t.setDate(t.getDate() + 1); setSelectedDate(t.toLocaleDateString("sv-SE")); }}
            style={actionBtn("#1a3a5c", T.blue)}>Amanhã</button>
          <span style={{ marginLeft: "auto", fontSize: 13, color: T.muted }}>
            Agenda de <strong style={{ color: T.text }}>{fmt(selectedDate)}</strong>
          </span>
          <button
            onClick={() => { fetchAgendamentos(); fetchDashboard(); fetchHistorico(); }}
            style={{ ...actionBtn("rgba(232,200,74,0.1)", T.gold), border: "1px solid rgba(232,200,74,0.3)", display: "flex", alignItems: "center", gap: 6 }}
          >
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>

        {/* ── METRIC CARDS ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12, marginBottom: 24 }}>
          {[
            { label: "Agendamentos", value: dashboard.totalAgendamentos, icon: <Calendar size={28} color={T.blue} />, grad: "linear-gradient(135deg,#0d1b2a,#1a3a5c)", border: "#1e4a78", color: T.blue },
            { label: "Buscar Veículo", value: dashboard.totalPickup, icon: <Truck size={28} color={T.orange} />, grad: "linear-gradient(135deg,#1a0e00,#3d2200)", border: "#7a4500", color: T.orange },
            { label: "Finalizados", value: dashboard.totalFinalizados, icon: <CheckCircle2 size={28} color={T.green} />, grad: "linear-gradient(135deg,#0d1a0a,#1a3a10)", border: "#2d6a2d", color: T.green },
          ].map(c => (
            <div key={c.label} style={{ ...T.card(c.grad, c.border), display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ margin: 0, fontSize: 12, color: T.muted }}>{c.label}</p>
                <h2 style={{ margin: "4px 0 0", fontSize: 32, fontWeight: 700, color: c.color }}>{c.value}</h2>
              </div>
              {c.icon}
            </div>
          ))}
        </div>

        {/* ── APPOINTMENTS ── */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: T.gold }}>Agendamentos do Dia</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ position: "relative" }}>
                <Search size={15} color={T.muted} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
                <input
                  type="text" placeholder="Buscar cliente..."
                  value={search} onChange={e => setSearch(e.target.value)}
                  style={{ ...inp, paddingLeft: 32, width: 200 }}
                />
              </div>
              <span style={{ ...pill(T.blue, "rgba(116,192,252,0.1)"), border: "1px solid rgba(116,192,252,0.2)" }}>
                {filtered.length} agendamentos
              </span>
            </div>
          </div>

          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <Loader2 size={28} color={T.gold} style={{ animation: "spin 1s linear infinite" }} />
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ ...T.card("#0d0d1a", "#222"), textAlign: "center", color: T.muted, padding: 40 }}>
              Nenhum agendamento encontrado.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {filtered.map(item => (
                <div key={item.id} style={{ ...T.card("linear-gradient(135deg,#0d0d1a,#111122)", "#1e2a3a"), transition: "border-color 0.2s" }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start", justifyContent: "space-between" }}>

                    {/* client info */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 160 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <User size={16} color={T.muted} />
                        <span style={{ fontWeight: 700, color: T.text, fontSize: 14 }}>{item.name}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.muted, fontSize: 13 }}>
                        <Phone size={14} /> {item.phone}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.muted, fontSize: 13 }}>
                        <Clock size={14} /> {item.time}
                      </div>
                      {item.vehicleType && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.muted, fontSize: 13 }}>
                          <Car size={14} /> {item.vehicleType}
                        </div>
                      )}
                      {item.address && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.muted, fontSize: 13 }}>
                          <MapPin size={14} /> {item.address}
                        </div>
                      )}
                    </div>

                    {/* badges */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
                      {item.pickup
                        ? <span style={pill(T.orange, "rgba(255,159,67,0.15)")}>🚚 Buscar veículo</span>
                        : <span style={pill(T.green, "rgba(105,219,124,0.15)")}>✔ Cliente leva</span>
                      }
                      <StatusBadge status={item.status} />
                    </div>

                    {/* actions */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                      <button onClick={() => updateStatus(item.id, "Em andamento")}
                        style={actionBtn(T.yellow)}>Em andamento</button>
                      <button onClick={() => updateStatus(item.id, "Finalizado")}
                        style={actionBtn(T.green)}>Finalizar</button>
                      <button onClick={() => updateStatus(item.id, "Cancelado")}
                        style={{ ...actionBtn("rgba(255,107,107,0.15)", T.red), border: "1px solid rgba(255,107,107,0.3)" }}>Cancelar</button>
                    </div>
                  </div>

                  {item.notes && (
                    <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(232,200,74,0.06)", border: "1px solid rgba(232,200,74,0.15)", borderRadius: 10, fontSize: 13, color: T.muted }}>
                      <strong style={{ color: T.gold }}>Obs:</strong> {item.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── HISTORY TABLE ── */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: T.gold }}>Histórico de Serviços</h2>
            <span style={{ ...pill(T.green, "rgba(105,219,124,0.1)"), border: "1px solid rgba(105,219,124,0.2)" }}>
              {historico.length} finalizados
            </span>
          </div>

          <div style={{ ...T.card("linear-gradient(135deg,#0d0d1a,#111122)", "#1e2a3a"), padding: 0, overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 800, borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #1e2a3a" }}>
                  {["Cliente", "Telefone", "Veículo", "Data", "Hora", "Pickup", "Status"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", color: T.muted, fontWeight: 600, fontSize: 12, letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {historico.map((item, i) => (
                  <tr key={item.id} style={{ borderTop: i > 0 ? "1px solid #1a1a2a" : "none" }}>
                    <td style={{ padding: "12px 16px", fontWeight: 600, color: T.text }}>{item.name}</td>
                    <td style={{ padding: "12px 16px", color: T.muted }}>{item.phone}</td>
                    <td style={{ padding: "12px 16px", color: T.muted }}>{item.vehicleType}</td>
                    <td style={{ padding: "12px 16px", color: T.muted }}>{fmt(item.date)}</td>
                    <td style={{ padding: "12px 16px", color: T.muted }}>{item.time}</td>
                    <td style={{ padding: "12px 16px" }}>
                      {item.pickup
                        ? <Truck size={16} color={T.orange} />
                        : <XCircle size={16} color="#444" />
                      }
                    </td>
                    <td style={{ padding: "12px 16px" }}><StatusBadge status={item.status} /></td>
                  </tr>
                ))}
                {historico.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 32, textAlign: "center", color: T.muted }}>Nenhum histórico disponível.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}